/**
 * Перетаскивание пальцем.
 *
 * Мобильные браузеры не порождают drag-события из касаний, поэтому вёрстка на
 * HTML5 Drag and Drop (канбан заявок, список этапов воронки) на телефоне не
 * работает вовсе: ни одно из dragstart/dragover/drop до обработчиков не
 * доходит. Модуль следит за касаниями внутри переданного корня и сам рассылает
 * ровно те же события, что браузер шлёт для мыши, — обработчики компонентов
 * остаются общими для мыши и пальца, а десктопное поведение не меняется.
 *
 * Соглашения настоящего DnD соблюдаются:
 * — источник ищется по ближайшему предку с draggable;
 * — preventDefault() в dragstart отменяет перенос (так CrmFunnelFormUi
 *   разрешает тащить строку только за ручку);
 * — dataTransfer настоящий, setData/effectAllowed работают как обычно.
 *
 * Указатель отслеживается pointer-, а не touch-событиями: только у первых есть
 * захват (setPointerCapture), который адресует весь поток корню, а не тому
 * элементу, где жест начался.
 *
 * ВАЖНОЕ ОГРАНИЧЕНИЕ, которое обязан соблюдать вызывающий код: пока идёт
 * перенос, узел, на котором началось касание, должен оставаться в DOM. Chrome
 * шлёт pointercancel в тот же момент, когда этот узел удаляют, и жест
 * умирает — захват тут не спасает, он перенаправляет доставку, но отменённый
 * указатель не воскрешает. Поэтому колонка-источник в канбане на время
 * переноса пальцем перестаёт перерисовываться (см. isTouchDragging и
 * CrmKanbanUi#isStageFrozen).
 */

// Идёт ли прямо сейчас перенос пальцем. Флаг общий на всё приложение —
// одновременно идёт не больше одного жеста
let dragging = false;

export function isTouchDragging() {
  return dragging;
}

// Сколько палец должен пролежать на месте, чтобы это считалось «взял
// карточку», а не «прокручиваю». Без задержки свайп по карточке уводил бы
// её саму, и прокрутить доску пальцем было бы негде
const HOLD_DELAY = 250;

/** Ближайший предок, который действительно прокручивается по этой оси. */
function scrollableAncestor(el, horizontal) {
  for (let node = el; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    const scrolls = horizontal
      ? /auto|scroll/.test(style.overflowX) && node.scrollWidth > node.clientWidth
      : /auto|scroll/.test(style.overflowY) && node.scrollHeight > node.clientHeight;

    if (scrolls) return node;
  }

  return null;
}

/** Страница целиком — запасной вариант, когда прокручиваемого предка нет. */
function pageScroller() {
  const el = document.scrollingElement;

  return el && el.scrollHeight > el.clientHeight ? el : null;
}

/**
 * Включает перенос пальцем внутри root. Возвращает функцию отключения —
 * вызвать её в beforeDestroy компонента.
 */
export default function enableTouchDrag(root) {
  // источник переноса и указатель, которым его тащат
  let source = null;
  let pointerId = null;
  let dataTransfer = null;
  // перенос начался: до этого момента идёт только отсчёт удержания
  let active = false;
  let holdTimer = null;
  // точка касания: стартовая, текущая и её смещение внутри карточки
  let startX = 0;
  let startY = 0;
  let x = 0;
  let y = 0;
  let grabX = 0;
  let grabY = 0;
  let ghost = null;
  // элемент под пальцем — ему адресуются dragenter/dragover/drop
  let hovered = null;
  let scrollX = null;
  let scrollY = null;
  let frame = null;

  // Возвращает false, если обработчик отменил событие — как и у настоящего
  // DnD, это отказ (в dragstart — тащить, в dragover — принимать)
  function dispatchDrag(type, target) {
    if (!target) return true;

    const event = new DragEvent(type, {
      bubbles: true,
      cancelable: true,
      composed: true,
      clientX: x,
      clientY: y,
      dataTransfer,
    });
    target.dispatchEvent(event);

    return !event.defaultPrevented;
  }

  function moveGhost() {
    ghost.style.transform = `translate(${x - grabX}px, ${y - grabY}px)`;
  }

  // За пальцем едет копия карточки: сам исходный элемент принадлежит Vue и
  // во время переноса перерисовывается, а то и удаляется совсем
  function addGhost() {
    const rect = source.getBoundingClientRect();
    grabX = x - rect.left;
    grabY = y - rect.top;

    ghost = source.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.left = '0';
    ghost.style.top = '0';
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.margin = '0';
    ghost.style.zIndex = '10000';
    ghost.style.opacity = '0.9';
    ghost.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.18)';
    // иначе elementFromPoint находил бы под пальцем саму копию
    ghost.style.pointerEvents = 'none';
    moveGhost();

    document.body.appendChild(ghost);
  }

  function updateHovered() {
    const target = document.elementFromPoint(x, y);

    if (target !== hovered) {
      dispatchDrag('dragleave', hovered);
      hovered = target;
      dispatchDrag('dragenter', hovered);
    }

    dispatchDrag('dragover', hovered);
  }

  function autoScrollTick() {
    frame = requestAnimationFrame(autoScrollTick);

    // У края прокручиваемой области палец гонит её сам: на телефоне в доску
    // помещается одна колонка, и перенести заявку иначе было бы некуда
    const edge = 60;
    const speed = 14;
    let scrolled = false;

    if (scrollX) {
      const rect = scrollX.getBoundingClientRect();
      const before = scrollX.scrollLeft;

      if (x < rect.left + edge) scrollX.scrollLeft -= speed;
      else if (x > rect.right - edge) scrollX.scrollLeft += speed;

      scrolled = scrollX.scrollLeft !== before;
    }

    if (scrollY) {
      // у прокрутки всей страницы рамка элемента — это высота документа,
      // а не окна, поэтому границы берём прямо из окна
      const rect = scrollY === document.scrollingElement
        ? { top: 0, bottom: window.innerHeight }
        : scrollY.getBoundingClientRect();
      const before = scrollY.scrollTop;

      if (y < rect.top + edge) scrollY.scrollTop -= speed;
      else if (y > rect.bottom - edge) scrollY.scrollTop += speed;

      scrolled = scrolled || scrollY.scrollTop !== before;
    }

    // палец мог остаться на месте, а вот под ним уже другая колонка
    if (scrolled) updateHovered();
  }

  // долгое нажатие на Android иначе открывает системное меню
  function onContextMenu(e) {
    e.preventDefault();
  }

  function reset() {
    clearTimeout(holdTimer);
    cancelAnimationFrame(frame);
    document.removeEventListener('contextmenu', onContextMenu);
    document.body.style.removeProperty('user-select');
    if (ghost) ghost.remove();
    if (active && root.hasPointerCapture(pointerId)) root.releasePointerCapture(pointerId);

    source = null;
    pointerId = null;
    dataTransfer = null;
    active = false;
    dragging = false;
    holdTimer = null;
    ghost = null;
    hovered = null;
    scrollX = null;
    scrollY = null;
    frame = null;
  }

  function begin() {
    holdTimer = null;

    dataTransfer = new DataTransfer();
    dataTransfer.effectAllowed = 'all';
    addGhost();

    if (!dispatchDrag('dragstart', source)) {
      // штатный отказ: тащить нечего, и жест возвращается странице —
      // touchmove мы не перехватывали, так что прокрутка ещё жива
      reset();
      return;
    }

    active = true;
    dragging = true;
    // Захват: без него события указателя продолжают адресоваться элементу,
    // на котором начался жест, и до корня доходят не всегда
    root.setPointerCapture(pointerId);
    document.body.style.userSelect = 'none';
    document.addEventListener('contextmenu', onContextMenu);

    scrollX = scrollableAncestor(source, true);
    scrollY = scrollableAncestor(source, false) ?? pageScroller();
    frame = requestAnimationFrame(autoScrollTick);

    updateHovered();
  }

  function onPointerDown(e) {
    // мышь и трекпад ведёт сам браузер — модуль нужен только касаниям
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    // Второе касание во время переноса отсеивается по isPrimary, так что сюда
    // мы попадаем с уже новым жестом. Если от прошлого что-то осталось, он
    // завершился аварийно (например, касание отменили) — начинаем с чистого
    // листа, иначе одна сорвавшаяся попытка заблокировала бы все следующие
    if (source) reset();

    const el = e.target.closest?.('[draggable]');
    if (!el?.draggable) return;

    source = el;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    x = startX;
    y = startY;
    holdTimer = setTimeout(begin, HOLD_DELAY);
  }

  function onPointerMove(e) {
    if (e.pointerId !== pointerId) return;

    if (!active) {
      // палец поехал раньше, чем сработало удержание, — это прокрутка,
      // а не перенос
      if (Math.hypot(e.clientX - startX, e.clientY - startY) > 10) reset();
      return;
    }

    x = e.clientX;
    y = e.clientY;
    moveGhost();
    updateHovered();
  }

  // Конец переноса обязан дойти до живого дерева: если исходный узел всё же
  // успели удалить, событие на нём не всплывёт никуда, состояние переноса
  // останется висеть, и доска после этого выглядит намертво «схваченной»
  function dispatchDragEnd() {
    dispatchDrag('dragend', source.isConnected ? source : root);
  }

  function onPointerUp(e) {
    if (e.pointerId !== pointerId) return;

    if (active) {
      x = e.clientX;
      y = e.clientY;
      updateHovered();
      dispatchDrag('drop', hovered);
      dispatchDragEnd();
    }

    reset();
  }

  function onPointerCancel(e) {
    if (e.pointerId !== pointerId) return;

    if (active) dispatchDragEnd();
    reset();
  }

  // Единственное, ради чего нужны touch-события: пока палец тащит карточку,
  // страница не должна прокручиваться под ней. Отменённый touchmove решает
  // это на весь жест, поэтому потерять его после удаления карточки не страшно
  function onTouchMove(e) {
    if (active) e.preventDefault();
  }

  root.addEventListener('pointerdown', onPointerDown);
  root.addEventListener('pointermove', onPointerMove);
  root.addEventListener('pointerup', onPointerUp);
  root.addEventListener('pointercancel', onPointerCancel);
  root.addEventListener('touchmove', onTouchMove, { passive: false });

  return () => {
    reset();
    root.removeEventListener('pointerdown', onPointerDown);
    root.removeEventListener('pointermove', onPointerMove);
    root.removeEventListener('pointerup', onPointerUp);
    root.removeEventListener('pointercancel', onPointerCancel);
    root.removeEventListener('touchmove', onTouchMove);
  };
}
